import React, { useState, useEffect } from 'react';
import { Copy, Check, Download, FileText, Code, Settings, Database } from 'lucide-react';

interface CodeViewerProps {
  files: Record<string, string>;
  projectName: string;
  onDownload: () => void;
}

interface FileInfo {
  name: string;
  path: string;
  content: string;
  language: string;
  icon: React.ComponentType<any>;
}

const CodeViewer: React.FC<CodeViewerProps> = ({ files, projectName, onDownload }) => {
  const [activeTab, setActiveTab] = useState<string>('');
  const [copiedFile, setCopiedFile] = useState<string | null>(null);
  const [fileList, setFileList] = useState<FileInfo[]>([]);

  useEffect(() => {
    // Process files and determine languages
    const processedFiles: FileInfo[] = Object.entries(files).map(([path, content]) => {
      const fileName = path.split('/').pop() || path;
      const language = getLanguageFromFileName(fileName);
      const icon = getFileIcon(fileName);
      
      return {
        name: fileName,
        path,
        content,
        language,
        icon
      };
    });

    // Sort files by type and importance
    const sortedFiles = processedFiles.sort((a, b) => {
      const order = ['server.py', 'client.py', 'schemas.py', 'README.md', 'pyproject.toml', 'requirements.txt'];
      const aIndex = order.indexOf(a.name);
      const bIndex = order.indexOf(b.name);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

    setFileList(sortedFiles);
    
    // Set default active tab to server.py or first file
    const defaultTab = sortedFiles.find(f => f.name === 'server.py')?.path || sortedFiles[0]?.path;
    if (defaultTab) setActiveTab(defaultTab);
  }, [files]);

  const getLanguageFromFileName = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py': return 'python';
      case 'md': return 'markdown';
      case 'json': return 'json';
      case 'toml': return 'toml';
      case 'txt': return 'text';
      case 'yml':
      case 'yaml': return 'yaml';
      case 'js':
      case 'jsx': return 'javascript';
      case 'ts':
      case 'tsx': return 'typescript';
      default: return 'text';
    }
  };

  const getFileIcon = (fileName: string): React.ComponentType<any> => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (fileName.toLowerCase().includes('readme')) return FileText;
    if (fileName.toLowerCase().includes('config')) return Settings;
    if (ext === 'py') return Code;
    if (ext === 'json' || ext === 'toml') return Settings;
    if (ext === 'sql' || fileName.includes('schema')) return Database;
    return FileText;
  };

  const copyToClipboard = async (content: string, fileName: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedFile(fileName);
      setTimeout(() => setCopiedFile(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const getLanguageColor = (language: string): string => {
    switch (language) {
      case 'python': return 'bg-blue-500/20 text-blue-300';
      case 'markdown': return 'bg-green-500/20 text-green-300';
      case 'json': return 'bg-yellow-500/20 text-yellow-300';
      case 'toml': return 'bg-purple-500/20 text-purple-300';
      case 'yaml': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const renderCodeWithHighlighting = (code: string, language: string) => {
    const lines = code.split('\n');
    
    interface MatchResult {
      start: number;
      end: number;
      text: string;
      style: React.CSSProperties;
      patternIndex: number;
    }

    interface Pattern {
      regex: RegExp;
      style: React.CSSProperties;
    }
    
    const highlightLine = (line: string, lang: string): React.ReactNode => {
      if (lang === 'python') {
        const patterns: Pattern[] = [
          { regex: /\b(def|class|import|from|async|await|return|if|else|elif|for|while|try|except|with|as|True|False|None)\b/g, style: { color: '#c678dd' } },
          { regex: /(["'])((?:\\.|(?!\1)[^\\])*?)\1/g, style: { color: '#98c379' } },
          { regex: /(#.*$)/g, style: { color: '#5c6370', fontStyle: 'italic' } },
          { regex: /(@\w+)/g, style: { color: '#e5c07b' } },
          { regex: /\b\d+\.?\d*\b/g, style: { color: '#d19a66' } }
        ];
        
        return renderWithPatterns(line, patterns);
      } else if (lang === 'json') {
        const patterns: Pattern[] = [
          { regex: /"([^"]+)"(\s*:)/g, style: { color: '#61afef' } },
          { regex: /:\s*"([^"]*)"/g, style: { color: '#98c379' } },
          { regex: /:\s*(\d+\.?\d*)/g, style: { color: '#d19a66' } },
          { regex: /\b(true|false|null)\b/g, style: { color: '#c678dd' } }
        ];
        
        return renderWithPatterns(line, patterns);
      } else {
        return <span>{line}</span>;
      }
    };
    
    const renderWithPatterns = (text: string, patterns: Pattern[]): React.ReactNode => {
      let lastIndex = 0;
      const elements: React.ReactNode[] = [];
      const matches: MatchResult[] = [];
      
      // Collect all matches
      patterns.forEach((pattern, patternIndex) => {
        let match;
        const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
        while ((match = regex.exec(text)) !== null) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            style: pattern.style,
            patternIndex
          });
        }
      });
      
      // Sort matches by start position
      matches.sort((a, b) => a.start - b.start);
      
      // Remove overlapping matches (keep first one)
      const filteredMatches: MatchResult[] = [];
      let lastEnd = 0;
      matches.forEach(match => {
        if (match.start >= lastEnd) {
          filteredMatches.push(match);
          lastEnd = match.end;
        }
      });
      
      filteredMatches.forEach((match, index) => {
        // Add text before match
        if (match.start > lastIndex) {
          elements.push(
            <span key={`text-${index}`}>
              {text.substring(lastIndex, match.start)}
            </span>
          );
        }
        
        // Add highlighted match
        elements.push(
          <span key={`match-${index}`} style={match.style}>
            {match.text}
          </span>
        );
        
        lastIndex = match.end;
      });
      
      // Add remaining text
      if (lastIndex < text.length) {
        elements.push(
          <span key="text-end">
            {text.substring(lastIndex)}
          </span>
        );
      }
      
      return elements.length > 0 ? <>{elements}</> : <span>{text}</span>;
    };
    
    return lines.map((line, index) => (
      <div key={index} className="flex hover:bg-gray-800/30">
        <span className="text-gray-500 text-right pr-4 select-none min-w-12 shrink-0">
          {index + 1}
        </span>
        <span className="flex-1 font-mono">
          {line.trim() ? highlightLine(line, language) : '\u00A0'}
        </span>
      </div>
    ));
  };

  const activeFile = fileList.find(f => f.path === activeTab);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-hpe-dark-surface rounded-lg border border-hpe-dark-border overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-hpe-dark-border flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold mb-1">Generated Project Files</h2>
            <p className="text-hpe-gray-400 text-sm">
              Preview your generated MCP server code before downloading
            </p>
          </div>
          <button
            onClick={onDownload}
            className="flex items-center gap-2 px-4 py-2 bg-hpe-primary hover:bg-hpe-primary-dark rounded-lg font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Download Project
          </button>
        </div>

        <div className="flex h-[600px]">
          {/* File Tabs Sidebar */}
          <div className="w-80 border-r border-hpe-dark-border bg-hpe-dark-bg flex flex-col">
            <div className="p-3 border-b border-hpe-dark-border">
              <h3 className="font-medium text-sm text-hpe-gray-300">Project Files</h3>
              <p className="text-xs text-hpe-gray-400 mt-1">{fileList.length} files generated</p>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {fileList.map((file) => {
                const Icon = file.icon;
                const isActive = activeTab === file.path;
                
                return (
                  <button
                    key={file.path}
                    onClick={() => setActiveTab(file.path)}
                    className={`w-full text-left p-3 hover:bg-hpe-dark-card transition-colors border-b border-hpe-dark-border/50 ${
                      isActive ? 'bg-hpe-dark-card border-l-2 border-l-hpe-primary' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-hpe-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {file.name}
                        </div>
                        <div className="text-xs text-hpe-gray-500 truncate">
                          {file.path}
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getLanguageColor(file.language)}`}>
                        {file.language}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Code Preview */}
          <div className="flex-1 flex flex-col">
            {activeFile && (
              <>
                {/* File Header */}
                <div className="p-3 border-b border-hpe-dark-border bg-hpe-dark-bg flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {React.createElement(activeFile.icon, { className: "w-4 h-4 text-hpe-gray-400" })}
                    <span className="font-medium">{activeFile.name}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getLanguageColor(activeFile.language)}`}>
                      {activeFile.language}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(activeFile.content, activeFile.name)}
                    className="flex items-center gap-2 px-3 py-1 text-hpe-gray-400 hover:text-hpe-primary hover:bg-hpe-dark-card rounded transition-colors"
                  >
                    {copiedFile === activeFile.name ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span className="text-sm">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-sm">Copy</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Code Content */}
                <div className="flex-1 overflow-auto bg-gray-900">
                  <div className="py-4 text-sm leading-relaxed">
                    {renderCodeWithHighlighting(activeFile.content, activeFile.language)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer Stats */}
        <div className="p-3 border-t border-hpe-dark-border bg-hpe-dark-bg">
          <div className="flex justify-between items-center text-sm text-hpe-gray-400">
            <span>
              {fileList.length} files • {Object.values(files).join('').length.toLocaleString()} characters
            </span>
            {activeFile && (
              <span>
                {activeFile.content.split('\n').length} lines • {activeFile.content.length.toLocaleString()} characters
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeViewer;